import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/Medication';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CategoryIcon from '@mui/icons-material/Category';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { searchApi, ApiError } from '../../../services/api';
import styles from './SearchModal.module.css';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery?: string;
}

interface Code {
  codeType: string;
  codeValue: string;
}

interface Suggestion {
  text: string;
  type: 'provider' | 'procedure' | 'medication' | 'symptom' | 'taxonomy';
  codes: Code[];
}


const SearchModal: FunctionComponent<SearchModalProps> = ({ isOpen, onClose, initialQuery = '' }) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState<'ai' | 'providers' | 'procedures' | 'medications'>('ai');
  const [isTextarea, setIsTextarea] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([
    'MRI Scan',
    'Dr. Emily Carter',
    'Blood Test',
    'Physical Therapy',
    'Colonoscopy'
  ]);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery(initialQuery);
      setIsTextarea(false);
      setIsExpanded(false);
      setSelectedIndex(-1);
    }
  }, [isOpen, initialQuery]);

  // Handle autocomplete suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length > 0) {
        try {
          const data = await searchApi.autocomplete({
            type: searchType,
            q: searchQuery
          });
          setSuggestions(data.suggestions || []);
          setSelectedIndex(-1);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          if (error instanceof ApiError) {
            console.error('API Error:', error.status, error.message);
          }
          setSuggestions([]);
          setSelectedIndex(-1);
        }
      } else {
        setSuggestions([]);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType]);

  // Check if we should switch to textarea
  useEffect(() => {
    if (searchQuery.length > 35 && !isTextarea) {
      setIsTextarea(true);
      // Set focus to end of text in textarea after transition
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(searchQuery.length, searchQuery.length);
        }
      }, 0);
    }
  }, [searchQuery, isTextarea]);

  const handleSearch = (codes?: Code[]) => {
    if (searchQuery.trim()) {
      // Add to recent searches
      setRecentSearches(prev => [searchQuery, ...prev.filter(s => s !== searchQuery)].slice(0, 5));
      
      // Check if we have suggestions or if this is a complex query
      const hasNoSuggestions = suggestions.length === 0;
      const isComplexQuery = isTextarea || searchQuery.includes(' and ') || searchQuery.includes(' with ') || searchQuery.split(' ').length > 5;
      
      if (hasNoSuggestions || isComplexQuery || searchType === 'ai') {
        // Navigate to AI search results for complex queries, no suggestions, or when AI tab is selected
        navigate(`/ai-followup?q=${encodeURIComponent(searchQuery)}`);
      } else {
        // Build URL with codes if provided
        let url = `/search?q=${encodeURIComponent(searchQuery)}&type=${searchType}`;
        if (codes && codes.length > 0) {
          // Add codes as query parameters
          codes.forEach(code => {
            url += `&${code.codeType}=${encodeURIComponent(code.codeValue)}`;
          });
        }
        
        // Navigate to regular search results
        navigate(url);
      }
      onClose();
    }
  };

  const handleRecentSearchClick = (search: string) => {
    setSearchQuery(search);
    handleSearch();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    navigateToSuggestion(suggestion);
  };

  const navigateToSuggestion = (suggestion: Suggestion) => {
    // Navigate based on suggestion type
    switch (suggestion.type) {
      case 'provider':
        // Find NPI code
        const npiCode = suggestion.codes.find(c => c.codeType === 'npi');
        if (npiCode) {
          navigate(`/provider/${npiCode.codeValue}`);
          onClose();
        }
        break;
      case 'procedure':
        // Navigate to procedure detail page
        const cptCode = suggestion.codes.find(c => c.codeType === 'cpt');
        if (cptCode) {
          navigate(`/procedure/${cptCode.codeValue}`);
          onClose();
        }
        break;
      case 'medication':
        // Navigate to medication detail page
        const ndcCode = suggestion.codes.find(c => c.codeType === 'ndc');
        if (ndcCode) {
          navigate(`/medication/${ndcCode.codeValue}`);
          onClose();
        }
        break;
      case 'symptom':
        // For symptoms, do a search
        setSearchQuery(suggestion.text);
        handleSearch(suggestion.codes);
        break;
      case 'taxonomy':
        // Navigate to specialty detail page
        const taxonomyCode = suggestion.codes.find(c => c.codeType === 'taxonomy');
        if (taxonomyCode) {
          navigate(`/specialty/${taxonomyCode.codeValue}`);
          onClose();
        }
        break;
    }
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'provider':
        return <PersonIcon style={{ fontSize: '18px', color: '#4a739c' }} />;
      case 'procedure':
        return <MedicalServicesIcon style={{ fontSize: '18px', color: '#4a739c' }} />;
      case 'medication':
        return <MedicationIcon style={{ fontSize: '18px', color: '#4a739c' }} />;
      case 'symptom':
        return <HelpOutlineIcon style={{ fontSize: '18px', color: '#4a739c' }} />;
      case 'taxonomy':
        return <CategoryIcon style={{ fontSize: '18px', color: '#4a739c' }} />;
      default:
        return <SearchIcon style={{ fontSize: '18px', color: '#4a739c' }} />;
    }
  };

  const clearSearchQuery = () => {
    setSearchQuery('');
    setSuggestions([]);
    setIsTextarea(false);
    setIsExpanded(false);
    setSelectedIndex(-1);
    if (inputRef.current) {
      inputRef.current.focus();
    } else if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (selectedIndex < suggestions.length - 1) {
          setSelectedIndex(selectedIndex + 1);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (selectedIndex > -1) {
          setSelectedIndex(selectedIndex - 1);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          navigateToSuggestion(suggestions[selectedIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setSelectedIndex(-1);
        break;
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContent} ${isExpanded ? styles.expanded : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalScrollableContent}>
          <div className={styles.header}>
            <h2 className={styles.title}>Search</h2>
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon style={{ fontSize: '20px' }} />
            </button>
          </div>

          <div className={styles.searchSection}>
          <div className={`${styles.searchInputWrapper} ${isTextarea ? styles.textareaWrapper : ''}`}>
            <div className={styles.searchIcon}>
              <SearchIcon style={{ fontSize: '20px', color: '#4a739c' }} />
            </div>
            {!isTextarea ? (
              <input
                ref={inputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search providers, procedures, or medications..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            ) : (
              <textarea
                ref={textareaRef}
                className={styles.searchTextarea}
                placeholder="Tell us more about your symptoms..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIndex(-1);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                      navigateToSuggestion(suggestions[selectedIndex]);
                    } else {
                      handleSearch();
                    }
                  } else if (e.key !== 'Enter') {
                    handleKeyDown(e);
                  }
                }}
                autoFocus
                rows={isExpanded ? 10 : 3}
              />
            )}
            {searchQuery && (
              <button className={styles.clearButton} onClick={clearSearchQuery}>
                <ClearIcon style={{ fontSize: '20px', color: '#4a739c' }} />
              </button>
            )}
            {isTextarea && (
              <button className={styles.expandButton} onClick={toggleExpand}>
                <img 
                  src="/images/expand_content_24dp_1F1F1F_FILL0_wght400_GRAD0_opsz24.svg" 
                  alt="Expand" 
                  style={{ width: '20px', height: '20px' }}
                />
              </button>
            )}
          </div>

          <div className={styles.filterTabs}>
            <button 
              className={`${styles.filterTab} ${searchType === 'ai' ? styles.active : ''}`}
              onClick={() => setSearchType('ai')}
            >
              AI
            </button>
            <button 
              className={`${styles.filterTab} ${searchType === 'providers' ? styles.active : ''}`}
              onClick={() => setSearchType('providers')}
            >
              Providers
            </button>
            <button 
              className={`${styles.filterTab} ${searchType === 'procedures' ? styles.active : ''}`}
              onClick={() => setSearchType('procedures')}
            >
              Procedures
            </button>
            <button 
              className={`${styles.filterTab} ${searchType === 'medications' ? styles.active : ''}`}
              onClick={() => setSearchType('medications')}
            >
              Medications
            </button>
          </div>
        </div>

        {/* Show autocomplete suggestions when typing */}
        {searchQuery.length > 0 && (
          <div className={styles.autocompleteSection}>
            {suggestions.length > 0 ? (
              <div className={styles.autocompleteList}>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className={`${styles.autocompleteItem} ${styles[`autocompleteItem--${suggestion.type}`]} ${
                      index === selectedIndex ? styles.selected : ''
                    }`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span style={{ marginRight: '12px', display: 'flex', alignItems: 'center' }}>
                      {getIconForType(suggestion.type)}
                    </span>
                    <span className={styles.suggestionText}>{suggestion.text}</span>
                    <span className={styles.suggestionType}>{suggestion.type}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className={styles.noSuggestionsMessage}>
                <p className={styles.aiHint}>
                  <AutoAwesomeIcon className={styles.aiHintIcon} />
                  Press Enter to get AI-powered care recommendations
                </p>
                <p className={styles.messageText}>
                  The more detail you provide, the better Mario can personalize your care plan.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Show recent searches and popular searches when not typing */}
        {searchQuery.length === 0 && (
          <>
            {recentSearches.length > 0 && (
              <div className={styles.recentSearches}>
                <div className={styles.recentHeader}>
                  <h3 className={styles.recentTitle}>Recent Searches</h3>
                  <button className={styles.clearButtonText} onClick={clearRecentSearches}>
                    Clear all
                  </button>
                </div>
                <div className={styles.recentList}>
                  {recentSearches.map((search, index) => (
                    <button
                      key={index}
                      className={styles.recentItem}
                      onClick={() => handleRecentSearchClick(search)}
                    >
                      <span className={styles.recentIcon}>
                        <AccessTimeIcon style={{ fontSize: '18px', color: '#4a739c' }} />
                      </span>
                      {search}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.suggestions}>
              <h3 className={styles.suggestionsTitle}>Popular Searches</h3>
              <div className={styles.suggestionsList}>
                <button className={styles.suggestionItem} onClick={() => handleRecentSearchClick('Annual Physical')}>
                  <span className={styles.suggestionIcon}>
                    <LocalHospitalIcon style={{ fontSize: '20px', color: '#4a739c' }} />
                  </span>
                  Annual Physical
                </button>
                <button className={styles.suggestionItem} onClick={() => handleRecentSearchClick('Flu Shot')}>
                  <span className={styles.suggestionIcon}>
                    <VaccinesIcon style={{ fontSize: '20px', color: '#4a739c' }} />
                  </span>
                  Flu Shot
                </button>
                <button className={styles.suggestionItem} onClick={() => handleRecentSearchClick('Dermatologist')}>
                  <span className={styles.suggestionIcon}>
                    <PersonIcon style={{ fontSize: '20px', color: '#4a739c' }} />
                  </span>
                  Dermatologist
                </button>
                <button className={styles.suggestionItem} onClick={() => handleRecentSearchClick('Urgent Care')}>
                  <span className={styles.suggestionIcon}>
                    <LocalHospitalOutlinedIcon style={{ fontSize: '20px', color: '#4a739c' }} />
                  </span>
                  Urgent Care Near Me
                </button>
              </div>
            </div>
          </>
        )}
        </div>

        <button 
          className={styles.searchButton}
          onClick={() => handleSearch()}
          disabled={!searchQuery.trim()}
        >
          Search
        </button>
      </div>
    </div>
  );
};

export default SearchModal;